'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowRight } from './Icon'
import { CASE_STUDIES } from '@/lib/case-studies'

export default function CaseStudies() {
  const trackRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const observer = new IntersectionObserver(
      (entries) => {
        // The card whose center is closest to the track's center is "active".
        let bestIndex = activeIndex
        let bestRatio = 0
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio
            const idx = Number((entry.target as HTMLElement).dataset.index)
            if (!Number.isNaN(idx)) bestIndex = idx
          }
        })
        if (bestIndex !== activeIndex) setActiveIndex(bestIndex)
      },
      {
        root: track,
        threshold: [0.5, 0.75, 1],
      }
    )

    cardRefs.current.forEach((el) => el && observer.observe(el))
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mouse drag-to-scroll. Touch is left to native overflow scrolling so we don't fight it.
  // The DRAG_THRESHOLD prevents click-on-card from firing when the user actually dragged.
  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    const DRAG_THRESHOLD = 6
    let isDown = false
    let startX = 0
    let startScrollLeft = 0
    let totalDx = 0
    let activePointerId: number | null = null

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return // let native touch scrolling handle it
      isDown = true
      totalDx = 0
      startX = e.pageX
      startScrollLeft = track.scrollLeft
      activePointerId = e.pointerId
      track.classList.add('is-dragging')
      // Disable scroll-snap during drag — snap fights the manual scroll feel and re-snaps on release
      track.style.scrollSnapType = 'none'
    }

    const onPointerMove = (e: PointerEvent) => {
      if (!isDown || e.pointerId !== activePointerId) return
      const dx = e.pageX - startX
      totalDx = dx
      track.scrollLeft = startScrollLeft - dx
    }

    const endDrag = () => {
      if (!isDown) return
      isDown = false
      activePointerId = null
      track.classList.remove('is-dragging')
      // Re-enable snap; the browser will snap to the nearest card on release
      track.style.scrollSnapType = ''
    }

    // Suppress the click that would otherwise fire on the card the user released over.
    // Capture phase + checking the threshold here covers <Link> navigation and any nested click handlers.
    const onClickCapture = (e: MouseEvent) => {
      if (Math.abs(totalDx) > DRAG_THRESHOLD) {
        e.preventDefault()
        e.stopPropagation()
        totalDx = 0
      }
    }

    track.addEventListener('pointerdown', onPointerDown)
    track.addEventListener('pointermove', onPointerMove)
    track.addEventListener('pointerup', endDrag)
    track.addEventListener('pointercancel', endDrag)
    track.addEventListener('pointerleave', endDrag)
    track.addEventListener('click', onClickCapture, true)

    return () => {
      track.removeEventListener('pointerdown', onPointerDown)
      track.removeEventListener('pointermove', onPointerMove)
      track.removeEventListener('pointerup', endDrag)
      track.removeEventListener('pointercancel', endDrag)
      track.removeEventListener('pointerleave', endDrag)
      track.removeEventListener('click', onClickCapture, true)
    }
  }, [])

  const goTo = (i: number) => {
    const card = cardRefs.current[i]
    if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
  }

  return (
    <section id="work" className="case-studies-section">
      <div className="container">
        <h2 className="case-studies-title font-logo">Selected Work</h2>
        <p className="case-studies-subtitle">
          Real engagements. Real outcomes. Names anonymized where NDAs apply.
        </p>

        <div
          ref={trackRef}
          className="case-studies-carousel"
          role="region"
          aria-label="Case studies carousel — scroll horizontally"
          tabIndex={0}
        >
          {CASE_STUDIES.map((c, i) => (
            <Link
              key={c.slug}
              href={`/work/${c.slug}`}
              className="case-study-card case-study-card-carousel"
              ref={(el) => {
                cardRefs.current[i] = el
              }}
              data-index={i}
              aria-label={`Case study: ${c.title}`}
            >
              <div className="case-study-meta">
                <span className="case-study-industry">{c.industry}</span>
                <span className="case-study-arrow" aria-hidden="true">
                  <ArrowRight />
                </span>
              </div>

              <h3 className="case-study-headline font-logo">{c.title}</h3>

              <p className="case-study-summary">{c.summary}</p>

              <ul className="case-study-tags" aria-label="Project tags">
                {c.tags.map((t) => (
                  <li key={t} className="case-study-tag">
                    {t}
                  </li>
                ))}
              </ul>

              <span className="case-study-link">
                Read case study <ArrowRight />
              </span>
            </Link>
          ))}
        </div>

        <div className="case-studies-dots" role="tablist" aria-label="Case studies pagination">
          {CASE_STUDIES.map((c, i) => (
            <button
              key={c.slug}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              aria-label={`Go to case study ${i + 1} of ${CASE_STUDIES.length}: ${c.title}`}
              className={`case-studies-dot ${i === activeIndex ? 'is-active' : ''}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        <div className="case-studies-footer">
          <Link href="/work" className="case-studies-all-link">
            See all work <ArrowRight />
          </Link>
        </div>
      </div>
    </section>
  )
}
